import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const GOLD = "#c9a46a";
const GREEN = "#4ade80";
const BLUE = "#60a5fa";

const COLOR_MAP = { "#c9a46a": GOLD, "#4ade80": GREEN, "#60a5fa": BLUE };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#0B2027",
      border: "1px solid rgba(201,164,106,0.35)",
      padding: "10px 14px",
      fontSize: 12,
      fontFamily: "Inter, sans-serif",
    }}>
      <p style={{ color: GOLD, fontFamily: "Cormorant Garamond, Georgia, serif", fontSize: 14, marginBottom: 6 }}>
        {label}
      </p>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ display: "flex", gap: 8, alignItems: "center", color: "rgba(255,255,255,0.75)", marginBottom: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: p.color, flexShrink: 0 }} />
          <span style={{ color: "rgba(255,255,255,0.45)" }}>{p.name}:</span>
          <span style={{ fontWeight: 600 }}>
            {typeof p.value === "number" && p.value >= 1000 ? p.value.toLocaleString() : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const TICK = { fill: "rgba(255,255,255,0.4)", fontSize: 10, fontFamily: "Inter, sans-serif" };

export default function SofiaChatChart({ chartData }) {
  if (!chartData || chartData.type !== "bar" || !chartData.data?.length) return null;

  const { title, subtitle, layout, data, bars, xKey } = chartData;
  const isHorizontal = layout === "horizontal";
  const hasRight = bars.some((b) => b.axis === "right");

  return (
    <div style={{
      marginTop: 12,
      border: "1px solid rgba(201,164,106,0.2)",
      background: "#0e2830",
      overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        padding: "12px 16px 10px",
        borderBottom: "1px solid rgba(201,164,106,0.12)",
      }}>
        <div style={{
          fontFamily: "Cormorant Garamond, Georgia, serif",
          fontSize: 15,
          fontWeight: 500,
          color: GOLD,
          lineHeight: 1.2,
        }}>
          {title}
        </div>
        {subtitle && (
          <div style={{
            fontSize: 9,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.28)",
            marginTop: 4,
            fontFamily: "Inter, sans-serif",
          }}>
            {subtitle}
          </div>
        )}
      </div>

      {/* Chart */}
      <div style={{ padding: "12px 8px 4px", height: isHorizontal ? 196 : 210 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout={isHorizontal ? "vertical" : "horizontal"}
            margin={{
              top: 0,
              right: hasRight ? 44 : 8,
              left: isHorizontal ? 104 : 4,
              bottom: 4,
            }}
            barCategoryGap="30%"
          >
            <CartesianGrid
              strokeDasharray="2 4"
              stroke="rgba(255,255,255,0.06)"
              vertical={!isHorizontal}
              horizontal={isHorizontal}
            />

            {isHorizontal ? (
              <>
                <XAxis type="number" tick={TICK} axisLine={false} tickLine={false} />
                <YAxis dataKey={xKey} type="category" tick={{ ...TICK, fontSize: 9 }} axisLine={false} tickLine={false} width={100} />
              </>
            ) : (
              <>
                <XAxis dataKey={xKey} tick={{ ...TICK, fontSize: 9 }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" tick={TICK} axisLine={false} tickLine={false} width={40} />
                {hasRight && (
                  <YAxis yAxisId="right" orientation="right" tick={TICK} axisLine={false} tickLine={false} width={40} />
                )}
              </>
            )}

            <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(201,164,106,0.06)" }} />
            <Legend
              wrapperStyle={{
                fontSize: 9,
                color: "rgba(255,255,255,0.4)",
                paddingTop: 6,
                fontFamily: "Inter, sans-serif",
                letterSpacing: "0.05em",
              }}
              iconSize={7}
              iconType="circle"
            />

            {bars.map((b) =>
              isHorizontal ? (
                <Bar
                  key={b.key}
                  dataKey={b.key}
                  name={b.label}
                  fill={COLOR_MAP[b.color] || b.color}
                  radius={[0, 2, 2, 0]}
                  maxBarSize={16}
                />
              ) : (
                <Bar
                  key={b.key}
                  dataKey={b.key}
                  name={b.label}
                  fill={COLOR_MAP[b.color] || b.color}
                  yAxisId={b.axis === "right" ? "right" : "left"}
                  radius={[2, 2, 0, 0]}
                  maxBarSize={24}
                />
              )
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Footer */}
      <div style={{
        padding: "6px 16px 10px",
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: 9,
        color: "rgba(255,255,255,0.2)",
        fontFamily: "Inter, sans-serif",
        letterSpacing: "0.12em",
        textTransform: "uppercase",
      }}>
        <span style={{ width: 4, height: 4, borderRadius: "50%", background: "rgba(201,164,106,0.35)" }} />
        Sofia Sands Realty · sofiasandsrealty.ae
      </div>
    </div>
  );
}
